/**
 * Subscription choice (PDP) – Figma 6510-20648
 *
 * Responsibilities:
 *  1. Subscribe / one-time radio toggle — enables or disables selling_plan
 *     submission with the product form.
 *  2. Reliable ATC — on init, a hidden <input name="selling_plan"> is injected
 *     directly inside the <form> element so FormData always picks it up,
 *     regardless of cross-DOM form-association edge-cases.
 *  3. Reactive prices — listens to Dawn's PUB_SUB_EVENTS.variantChange event
 *     and updates the displayed subscribe price using the new variant's
 *     selling_plan_allocations. Also refreshes when the frequency select changes.
 */
(function () {
  /* ------------------------------------------------------------------ */
  /*  Money formatting                                                    */
  /* ------------------------------------------------------------------ */

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var noDecimals = String(Math.round(cents / 100));
    return (format || '${{amount}}')
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals);
  }

  /* ------------------------------------------------------------------ */
  /*  Price display                                                       */
  /* ------------------------------------------------------------------ */

  function updatePrices(wrapper, variant, sellingPlanId) {
    var priceEl = wrapper.querySelector('.soul-subscription-choice__price');
    if (!priceEl) return;

    var moneyFormat = wrapper.dataset.moneyFormat || '${{amount}}';
    var allocations = (variant && variant.selling_plan_allocations) || [];
    var spId = parseInt(sellingPlanId, 10);
    var allocation = null;

    for (var i = 0; i < allocations.length; i++) {
      if (allocations[i].selling_plan_id === spId) {
        allocation = allocations[i];
        break;
      }
    }

    if (allocation) {
      priceEl.textContent = formatMoney(allocation.price, moneyFormat);

      // Update compare-at price
      var compareEl = wrapper.querySelector('.soul-subscription-choice__compare-price');
      var base = allocation.compare_at_price;
      if (!base || base <= allocation.price) base = variant ? variant.price : 0;
      if (compareEl) {
        if (base > allocation.price) {
          compareEl.textContent = formatMoney(base, moneyFormat);
          compareEl.hidden = false;
          compareEl.style.display = '';
        } else {
          compareEl.hidden = true;
          compareEl.style.display = 'none';
        }
      }

      // Update the "Save X%" benefit span if present
      var discountEl = wrapper.querySelector('[data-discount-benefit]');
      if (discountEl) {
        if (base > allocation.price) {
          var pct = Math.round((base - allocation.price) * 100 / base);
          discountEl.textContent = 'Save ' + pct + '%';
        }
      }
    } else if (variant) {
      // One-time purchase = standard retail price (matches cart/checkout)
      priceEl.textContent = formatMoney(variant.price, moneyFormat);

      // Only show a strikethrough when the variant has a genuine compare-at price
      var compareEl = wrapper.querySelector('.soul-subscription-choice__compare-price');
      if (compareEl) {
        var otBase = variant.compare_at_price;
        if (otBase && otBase > variant.price) {
          compareEl.textContent = formatMoney(otBase, moneyFormat);
          compareEl.hidden = false;
          compareEl.style.display = '';
        } else {
          compareEl.hidden = true;
          compareEl.style.display = 'none';
        }
      }
    }

    // Update price per serve (selling price / serve count)
    // Uses variant.option3 if it's a positive number (e.g. drinks: 6, 12, 24 cans); otherwise 30
    var perServeEl = wrapper.querySelector('[data-price-per-serve]');
    if (perServeEl) {
      var priceForServe = allocation ? allocation.price : (variant ? variant.price : 0);
      if (priceForServe > 0) {
        var serveCount = parseInt(variant && variant.option3, 10);
        if (!serveCount || serveCount <= 0) serveCount = 30;
        perServeEl.textContent = formatMoney(Math.round(priceForServe / serveCount), moneyFormat) + ' p/serve';
      }
    }

    // Sync ATC button price with whichever mode is selected
    var submitPriceEl = wrapper._submitPriceEl;
    if (submitPriceEl && variant) {
      var checkedRadio = wrapper.querySelector('[data-subscription-radio]:checked');
      var isSubscribe = checkedRadio && checkedRadio.value === 'subscribe';
      if (isSubscribe && allocation) {
        submitPriceEl.textContent = ' \u2022 ' + formatMoney(allocation.price, moneyFormat);
      } else {
        var oneTimeText = ' \u2022 ' + formatMoney(variant.price, moneyFormat);
        submitPriceEl.textContent = oneTimeText;
        wrapper._oneTimePriceText = oneTimeText;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Per-wrapper initialisation                                          */
  /* ------------------------------------------------------------------ */

  function initSubscriptionChoice(wrapper) {
    var radios = wrapper.querySelectorAll('[data-subscription-radio]');
    var sellingPlanSelect = wrapper.querySelector('[data-frequency-select]');
    var details = wrapper.querySelector('[data-subscription-details]');

    if (!radios.length) return;

    /* --- 1. Locate product form and inject hidden input --------------- */
    var productForm = null;
    var hiddenInput = null;

    if (sellingPlanSelect) {
      // When subscription choice is inside the form, closest('form') finds it; otherwise use form attribute
      productForm = sellingPlanSelect.closest('form') || (function () {
        var formId = sellingPlanSelect.getAttribute('form');
        return formId ? document.getElementById(formId) : null;
      })();
    }

    if (productForm) {
      // Avoid double-injection if init runs twice (e.g. theme editor)
      hiddenInput = productForm.querySelector('[data-soul-sp-input]');
      if (!hiddenInput) {
        hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.setAttribute('data-soul-sp-input', '');
        productForm.appendChild(hiddenInput);
      }

      // The visible select is now UI-only; the hidden input owns submission.
      sellingPlanSelect.removeAttribute('name');
      sellingPlanSelect.removeAttribute('form');
    }

    // Cache ATC button price element and store the initial (one-time) price text
    wrapper._submitPriceEl = productForm
      ? productForm.querySelector('[data-product-form-submit-price]')
      : null;
    if (wrapper._submitPriceEl) {
      var basePrice = parseInt(wrapper._submitPriceEl.dataset.basePrice, 10);
      var initMoneyFmt = wrapper.dataset.moneyFormat || '${{amount}}';
      wrapper._oneTimePriceText = basePrice > 0
        ? ' • ' + formatMoney(basePrice, initMoneyFmt)
        : wrapper._submitPriceEl.textContent;
    }

    /* --- 2. Validate selling plan options ----------------------------- */
    var hasValidOptions = sellingPlanSelect && Array.prototype.some.call(
      sellingPlanSelect.options,
      function (opt) { return /^\d+$/.test(opt.value); }
    );

    /* --- 3. Apply state (toggle + hidden input + details pane) -------- */
    function applyState(value) {
      var isSubscribe = value === 'subscribe';

      if (hiddenInput) {
        if (isSubscribe && sellingPlanSelect && sellingPlanSelect.value) {
          hiddenInput.name = 'selling_plan';
          hiddenInput.value = sellingPlanSelect.value;
        } else {
          hiddenInput.name = '';
          hiddenInput.value = '';
        }
      } else if (sellingPlanSelect) {
        // Fallback: no form found – fall back to the form-association approach
        sellingPlanSelect.disabled = !isSubscribe;
        sellingPlanSelect.name = isSubscribe ? 'selling_plan' : '';
      }

      if (details) {
        details.hidden = !isSubscribe;
      }

      // Sync ATC button price when toggling subscription mode
      var submitPriceEl = wrapper._submitPriceEl;
      if (submitPriceEl) {
        var cached = wrapper._cachedVariant;
        if (cached) {
          updatePrices(wrapper, cached, sellingPlanSelect ? sellingPlanSelect.value : null);
        } else if (isSubscribe) {
          // No variant cached yet; read from the subscription widget's Liquid-rendered price
          var subPriceEl = wrapper.querySelector('.soul-subscription-choice__price');
          if (subPriceEl && subPriceEl.textContent.trim()) {
            submitPriceEl.textContent = ' \u2022 ' + subPriceEl.textContent.trim();
          }
        } else {
          if (wrapper._oneTimePriceText !== undefined) {
            submitPriceEl.textContent = wrapper._oneTimePriceText;
          }
        }
      }
    }

    /* --- 4. Initial state --------------------------------------------- */
    var checkedRadio = wrapper.querySelector('[data-subscription-radio]:checked');
    var initialMode = checkedRadio ? checkedRadio.value : 'subscribe';
    if (!hasValidOptions) initialMode = 'onetime';
    applyState(initialMode);

    /* --- 5. Radio change listeners ------------------------------------ */
    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        applyState(radio.value);
      });
    });

    /* --- 6. Frequency select change – sync hidden input + price ------- */
    if (sellingPlanSelect) {
      sellingPlanSelect.addEventListener('change', function () {
        if (hiddenInput && hiddenInput.name === 'selling_plan') {
          hiddenInput.value = sellingPlanSelect.value;
        }
        // Trigger a visual price refresh using the cached variant (if any)
        var cached = wrapper._cachedVariant;
        if (cached) {
          updatePrices(wrapper, cached, sellingPlanSelect.value);
        }
      });
    }

    /* --- 7. Store select reference on wrapper for variant-change hook - */
    wrapper._sellingPlanSelect = sellingPlanSelect;
  }

  /* ------------------------------------------------------------------ */
  /*  Global variant-change listener                                     */
  /* ------------------------------------------------------------------ */

  function wireVariantChangeListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var sectionId = event && event.data && event.data.sectionId;
      var variant   = event && event.data && event.data.variant;
      if (!sectionId || !variant) return;

      // Find the wrapper associated with this section's product form
      var formId = 'product-form-' + sectionId;
      var hiddenInput = document.querySelector(
        '#' + formId + ' [data-soul-sp-input]'
      );

      var wrapper;
      if (hiddenInput) {
        // Walk up from the form to find the sibling subscription widget.
        // The widget is somewhere in the same product-info container.
        var productInfoSection = document.getElementById('ProductInfo-' + sectionId);
        if (productInfoSection) {
          wrapper = productInfoSection.querySelector('[data-subscription-choice]');
        }
      }

      if (!wrapper) return;

      // Cache the latest variant on the wrapper for frequency-select changes
      wrapper._cachedVariant = variant;

      var sellingPlanSelect = wrapper._sellingPlanSelect;
      var currentPlanId = sellingPlanSelect ? sellingPlanSelect.value : null;
      updatePrices(wrapper, variant, currentPlanId);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-subscription-choice]').forEach(initSubscriptionChoice);
    wireVariantChangeListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
